import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    forwardRef,
    OnDestroy,
    OnInit,
    inject,
    input,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
    EntityFilter,
    EntityFilterCollection,
    EntityFilterConstants,
} from '../../../admin/models/entity-filter';
import { EntityFilterType } from '../../../shared-platform/models/constants/entity-filter-type';
import { SubSink } from 'subsink';
import { UserService } from '../../../admin/services/user.service';
import { TooltipsModule } from '@progress/kendo-angular-tooltip';
import {
    SplitButtonModule,
    DropDownButtonModule,
} from '@progress/kendo-angular-buttons';
import { SVGIcon } from '@progress/kendo-svg-icons';
import { JsonPipe, CommonModule } from '@angular/common';
import { PermissionNames } from '../../../admin/models/permission-names';

//<!-- FormGroup consumer -->
//<div formGroupName="common">
//    <app-entities-filter formControlName="customers"></app-entities-filter>
//</div>

//<!-- ngModel consumer -->
//<app-entities-filter [(ngModel)]="selectedFilters"></app-entities-filter>
@Component({
    selector: 'app-entities-filter',
    templateUrl: './entities-filter.component.html',
    styleUrls: ['./entities-filter.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        TooltipsModule,
        JsonPipe,
        SplitButtonModule,
        DropDownButtonModule,
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => EntitiesFilterComponent),
            multi: true,
        },
    ],
})
export class EntitiesFilterComponent
    implements OnInit, OnDestroy, ControlValueAccessor
{
    private userService = inject(UserService);
    private cdr = inject(ChangeDetectorRef);

    protected hasShippersAccess: boolean = false;

    get hasSingleAssociation(): boolean {
        return this.filterCollection.associations.length === 1;
    }

    get hasMultipleAssociations(): boolean {
        return this.filterCollection.associations.length > 1;
    }

    get associationToggleText(): string {
        if (this.hasSingleAssociation) {
            return this.filterCollection.associations[0].name + ' Data';
        }

        // Only one Association selection is allowed
        const selected = this.selectedFilters.find(
            (filter) =>
                filter.entityFilterType === EntityFilterType.Association,
        );

        return selected ? `${selected.name} Data` : 'Select Association';
    }

    get isAssociationSelected(): boolean {
        return this.selectedFilters.some(
            (f) => f.entityFilterType === EntityFilterType.Association,
        );
    }

    get hasSingleShipper(): boolean {
        return (
            this.filterCollection.customers.length === 1 &&
            this.hasShippersAccess
        );
    }

    get hasMultipleShippers(): boolean {
        return (
            this.filterCollection.customers.length > 1 && this.hasShippersAccess
        );
    }

    get shipperToggleText(): string {
        const selected = this.selectedFilters.find(
            (filter) => filter.entityFilterType === EntityFilterType.Shipper,
        );
        return selected ? `${selected.name} Data` : 'Select Shipper';
    }

    get isAssociationDropdownDisabled(): boolean {
        if (this.isAssociationSelected) return false;
        return !this.canSelect;
    }

    get isShipperDropdownDisabled(): boolean {
        if (this.isShipperSelected) return false;
        return !this.canSelect;
    }

    get isShipperSelected(): boolean {
        return this.selectedFilters.some(
            (f) => f.entityFilterType === EntityFilterType.Shipper,
        );
    }

    get selectedFiltersText(): string {
        let copiedValues = [...this.selectedFilters];

        copiedValues = copiedValues.map((filter) => {
            switch (filter.entityFilterType) {
                case EntityFilterType.Shipper:
                case EntityFilterType.Association:
                    return {
                        entityFilterType: filter.entityFilterType,
                        entityId: filter.entityId,
                        name: `${filter.name} Data`,
                    };
            }

            return filter;
        });

        if (this.hasSingleAssociation) {
            copiedValues = copiedValues.map((item) => {
                if (item.entityFilterType === EntityFilterType.Association) {
                    return {
                        entityFilterType: EntityFilterType.Association,
                        entityId: item.entityId,
                        name: item.name,
                    };
                } else {
                    return item;
                }
            });
        }

        return copiedValues.map((filter) => filter.name).join(' | ');
    }

    public readonly showDebug = input<boolean>(false);

    public readonly TOOLTIP_MIN = 'At least 1 selection is required';
    public readonly TOOLTIP_MAX =
        'Maximum of 2 selections reached — deselect an item first';

    public readonly collapseDownIcon: SVGIcon = {
        name: 'collapse-down',
        content: `<path d="M8.11973 9.29006L11.9997 13.1701L15.8797 9.29006C16.2697 8.90006 16.8997 8.90006 17.2897 9.29006C17.6797 9.68006 17.6797 10.3101 17.2897 10.7001L12.6997 15.2901C12.3097 15.6801 11.6797 15.6801 11.2897 15.2901L6.69973 10.7001C6.30973 10.3101 6.30973 9.68006 6.69973 9.29006C7.08973 8.91006 7.72973 8.90006 8.11973 9.29006Z" fill="currentColor"/>`,
        viewBox: '0 0 24 24',
    };

    public filterCollection: EntityFilterCollection =
        new EntityFilterCollection();
    public isDisabled: boolean = false;
    private _selectedFilters: EntityFilter[] = [];

    private static readonly filterOrder: Record<string, number> = {
        [EntityFilterType.Shipper]: 0,
        [EntityFilterType.Association]: 1,
        [EntityFilterType.Network]: 2,
    };

    public get selectedFilters(): EntityFilter[] {
        return [...this._selectedFilters];
    }

    public set selectedFilters(value: EntityFilter[]) {
        this._selectedFilters = [...(value ?? [])].sort(
            (a, b) =>
                EntitiesFilterComponent.filterOrder[a.entityFilterType] -
                EntitiesFilterComponent.filterOrder[b.entityFilterType],
        );
    }

    private sub = new SubSink();

    constructor() {
        this.filterCollection.push(EntityFilterConstants.Network);
    }

    ngOnInit(): void {
        this.sub.sink = this.userService.getCurrentUser().subscribe((user) => {
            if (!user) {
                return;
            }

            this.hasShippersAccess = user.hasPermission(
                PermissionNames.RailIndustry.Shippers.Read,
            );

            let filters: EntityFilter[] = [];

            user.shippers.forEach((shipper) => {
                filters.push(
                    new EntityFilter(
                        shipper.globalId,
                        shipper.name,
                        EntityFilterType.Shipper,
                    ),
                );
            });

            user.associations.forEach((association) => {
                filters.push(
                    new EntityFilter(
                        association.acronym,
                        association.name,
                        EntityFilterType.Association,
                    ),
                );
            });

            filters = filters.sort((a, b) => a.name.localeCompare(b.name));

            const distinctFilters = [
                ...new Map(
                    [...this.filterCollection, ...filters].map((f) => [
                        f.entityId,
                        f,
                    ]),
                ).values(),
            ];

            this.filterCollection = new EntityFilterCollection(
                ...distinctFilters,
            );
            this.cdr.markForCheck();
        });
    }

    ngOnDestroy(): void {
        this.sub.unsubscribe();
    }

    getFilterTooltip(filter: EntityFilter): string {
        if (this.isFilterDisabled(filter)) {
            return this.isSelected(filter)
                ? this.TOOLTIP_MIN
                : this.TOOLTIP_MAX;
        }

        switch (filter.entityFilterType) {
            case EntityFilterType.Shipper:
                return 'Shipper specific data.';
            case EntityFilterType.Association:
                return 'Association specific data';
            default:
                // Network or any other non-disabled filter type shows network tooltip.
                return 'Anonymized and Aggregate network data.';
        }
    }

    getAssociationItemTooltip(filter: EntityFilter): string {
        if (!this.isAssociationItemDisabled(filter)) return '';
        return this.isSelected(filter) ? this.TOOLTIP_MIN : this.TOOLTIP_MAX;
    }

    getCustomerItemTooltip(filter: EntityFilter): string {
        if (!this.isCustomerItemDisabled(filter)) return '';
        return this.isSelected(filter) ? this.TOOLTIP_MIN : this.TOOLTIP_MAX;
    }

    isSelected(filter: EntityFilter): boolean {
        return this.selectedFilters.some((f) => f.entityId === filter.entityId);
    }

    isFilterDisabled(filter: EntityFilter): boolean {
        if (this.isSelected(filter)) return !this.canDeselect;
        return !this.canSelect;
    }

    isAssociationItemDisabled(filter: EntityFilter): boolean {
        if (this.isSelected(filter)) return !this.canDeselect;
        return (
            this.selectedFilters.filter(
                (f) => f.entityFilterType !== EntityFilterType.Association,
            ).length >= 2
        );
    }

    isCustomerItemDisabled(filter: EntityFilter): boolean {
        if (this.isSelected(filter)) return !this.canDeselect;
        return (
            this.selectedFilters.filter(
                (f) => f.entityFilterType !== EntityFilterType.Shipper,
            ).length >= 2
        );
    }

    onNetworkClick(): void {
        this.toggleFilter(this.filterCollection.network!);
    }

    onAssociationClick(filter: EntityFilter): void {
        this.toggleFilter(filter);
    }

    onShipperClick(filter: EntityFilter): void {
        this.toggleFilter(filter);
    }

    onShippersClick(): void {
        if (!this.isShipperSelected || !this.canDeselect) {
            return;
        }

        this.selectedFilters = this.selectedFilters.filter(
            (f) => f.entityFilterType !== EntityFilterType.Shipper,
        );
        this.onChange(this.selectedFilters);
        this.onTouched();
    }

    onShippersItemClick(event: EntityFilter): void {
        if (event) {
            this.onCustomerSelected(event);
        }
    }

    onAssociationsClick(): void {
        if (!this.isAssociationSelected || !this.canDeselect) {
            return;
        }

        this.selectedFilters = this.selectedFilters.filter(
            (f) => f.entityFilterType !== EntityFilterType.Association,
        );
        this.onChange(this.selectedFilters);
        this.onTouched();
    }

    onAssociationsItemClick(event: EntityFilter): void {
        if (event) {
            this.onAssociationSelected(event);
        }
    }

    onAssociationSelected(filter: EntityFilter): void {
        if (this.isSelected(filter) && !this.canDeselect) return;

        const withoutAssociations = this.selectedFilters.filter(
            (f) => f.entityFilterType !== EntityFilterType.Association,
        );

        if (!this.isSelected(filter) && withoutAssociations.length >= 2) return;

        this.selectedFilters = this.isSelected(filter)
            ? withoutAssociations
            : [...withoutAssociations, filter];

        this.onChange(this.selectedFilters);
        this.onTouched();
    }

    onCustomerSelected(filter: EntityFilter): void {
        if (this.isSelected(filter) && !this.canDeselect) return;

        const withoutCustomers = this.selectedFilters.filter(
            (f) => f.entityFilterType !== EntityFilterType.Shipper,
        );

        if (!this.isSelected(filter) && withoutCustomers.length >= 2) return;

        this.selectedFilters = this.isSelected(filter)
            ? withoutCustomers
            : [...withoutCustomers, filter];

        this.onChange(this.selectedFilters);
        this.onTouched();
    }

    registerOnChange(fn: (value: EntityFilter[]) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    writeValue(value: EntityFilter[] | null | undefined): void {
        this.selectedFilters = value ?? [];
        this.cdr.markForCheck();
    }

    private onChange: (value: EntityFilter[]) => void = () => {};
    private onTouched: () => void = () => {};

    private get canDeselect(): boolean {
        return this.selectedFilters.length > 1;
    }

    private get canSelect(): boolean {
        return this.selectedFilters.length < 2;
    }

    private toggleFilter(filter: EntityFilter): void {
        if (this.isSelected(filter)) {
            if (!this.canDeselect) return;
            this.selectedFilters = this.selectedFilters.filter(
                (f) => f.entityId !== filter.entityId,
            );
        } else {
            if (!this.canSelect) return;
            this.selectedFilters = [...this.selectedFilters, filter];
        }

        this.onChange(this.selectedFilters);
        this.onTouched();
    }
}
